import React, { useContext, useEffect } from 'react';
import axios from 'axios';
import { Route, Switch, Redirect, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withStyles } from '@material-ui/styles';

import AuthenticatedRoute from 'components/AuthenticatedRoute';
import UnauthenticatedRoute from 'components/UnauthenticatedRoute';
import MainWrapper from 'components/MainWrapper';
import Layout from 'components/Layout/index';

import NotFound404 from 'containers/InterstitialPages/404/index';
import LogIn from 'containers/LogIn/index';
import ForgotPassword from 'containers/ForgotPassword/index';
import ResetPassword from 'containers/ResetPassword/index';
import { AccountList, AccountDetail } from 'containers/Accounts/index';
import { CampaignDetail, CampaignList } from 'containers/Campaigns/index';
import { SequenceDetail } from 'containers/Sequences';
import Inbox from 'containers/Inbox/index';
import Reports from 'containers/Reports/index';
import Dashboard from 'containers/Dashboard/index';
import Coverage from 'containers/Coverage';
import Opportunities from 'containers/Opportunities';
import Timeline from 'containers/Timelines';
import { Container } from 'reactstrap';
import Profile from 'components/Layout/Profile';
import InboxDetail from 'components/Inbox/InboxDetail';
import { toggleInboxDetail } from 'redux/actions/messageActions';
import { MIN_DESKTOP_WIDTH, MAX_MEDIUM_SIZE } from 'constants/misc';
import { SidebarProps } from 'utils/prop-types/ReducerProps';
import { changeSidebarVisibility } from 'redux/actions/sidebarActions';
import { AbilityContext } from 'utils/permissions';
import Errors from 'components/Errors';
import MyInfo from 'containers/MyAccount/index';
import EmailVerification from 'containers/EmailVerification/index';
import { PAGES_TRACK } from 'constants/analytics';
import classNames from 'classnames';
import PermRoute from '../components/PermRoute';
import { useDocumentTitleObserveRouting } from '../utils/hooks';
import { CountRottingLeads } from '../components/CountRottingLeads';


// configuration for axios
(() => {
  // because of Safari https://stackoverflow.com/a/44479078/5751147
  axios.defaults.xsrfHeaderName = 'X-CSRFTOKEN';
  axios.defaults.xsrfCookieName = 'csrftoken';
})();

const styles = theme => ({
  container_wrap: {
    display: 'flex',
  },
  container: {
    paddingTop: '2rem',
    width: '100%',
    transition: 'all 0.3s',
    [`@media screen and (max-width: ${MIN_DESKTOP_WIDTH}px)`]: {
      paddingTop: '10rem',
    },
    '&$openedInboxDetail': {
      width: '50%',
      [`@media screen and (max-width: ${MAX_MEDIUM_SIZE}px)`]: {
        width: '100%',
      },
    },
  },
  backdrop: {
    display: 'none',
    [`@media screen and (min-width: ${MIN_DESKTOP_WIDTH}px) and (max-width: ${MAX_MEDIUM_SIZE}px)`]: {
      display: 'block',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: theme.palette.backdrop,
      opacity: 0.6,
      zIndex: 98,
    },
  },
  openedInboxDetail: {},
});


let wrappedRoutes = (props) => {
  const ability = useContext(AbilityContext);
  return (
    <div>
      <CountRottingLeads>

        <Layout />
        <div className={`${props.classes.container_wrap} container__wrap`}>
          {!props.sidebar.collapse &&
          <div
            className={props.classes.backdrop}
            onClick={props.toggleSidebarVisibility}
            role="button"
            tabIndex={-1}
            onKeyDown={() => {}}
          />
        }
          <div className={
            classNames(
                props.classes.container,
                { [props.classes.openedInboxDetail]: props.message.isOpen },
            )
            }
          >
            <Container>
              <Profile />
            </Container>
            <Switch>
              <Route
                exact
                path="/"
              >
                {ability.can('see', 'Campaign Selector')
              ? <Redirect to="/reports" />
              : <Redirect to="/dashboard" />}
              </Route>
              {ability.can('see', 'Account') && <Route exact path="/accounts" component={AccountList} />}
              <Route path="/accounts/:id(\d+)" component={AccountDetail} />
              <Route exact path="/campaigns" component={CampaignList} />
              <Route path="/campaigns/:id(\d+)" component={CampaignDetail} />
              <Route path="/sequences/:id(\d+)" component={SequenceDetail} />
              {/* <PermRoute path="/inbox" component={Inbox} perms={['see', 'Inbox']} /> */}
              <PermRoute path="/inbox/:id?" component={Inbox} perms={['see', 'Inbox']} />
              <Route path="/reports" component={Reports} />
              <Route path="/dashboard" component={Dashboard} />
              <Route path="/errors" component={Errors} />
              <PermRoute path="/coverage" component={Coverage} perms={['see', 'Coverage']} />
              <PermRoute path="/opportunities" component={Opportunities} perms={['see', 'Opportunity']} />
              <Route path="/myaccount" component={MyInfo} />
              <Route path="/account-confirm-email" component={EmailVerification} />
              <Route path="/timelines" component={Timeline} />
              <Redirect to="/404" />
            </Switch>
          </div>
          <InboxDetail message={props.message} loading={props.messages.loading} toggleInboxDetail={props.emptyMsg} />
        </div>
      </CountRottingLeads>
    </div>
  );
};

wrappedRoutes = withStyles(styles)(wrappedRoutes);


const Router = (props) => {
  const {
    childProps, message, messages, toggleInboxDetail: emptyMsg,
    sidebar, toggleSidebarVisibility, accounts,
  } = props;
  useDocumentTitleObserveRouting(props.location.pathname);
  useEffect(() => {
    const unlisten = props.history.listen((location) => {
      if (PAGES_TRACK.find(path => location.pathname.match(path))) {
        // eslint-disable-next-line no-undef
        analytics.page(location.pathname);
      }
    });
    return () => unlisten();
  }, []);

  return (
    <MainWrapper>
      <main>
        <Switch>
          <Route path="/404" component={NotFound404} />
          <Route path="/signup/managed-service" props={childProps} component={LogIn} />
          <Route path="/password/reset/confirm" component={ResetPassword} />
          <Route path="/forgot" component={ForgotPassword} />
          <UnauthenticatedRoute props={childProps} path="/login" component={LogIn} />
          <AuthenticatedRoute
            props={{
              ...childProps,
              toggleSidebarVisibility,
              message,
              messages,
              emptyMsg,
              sidebar,
              accounts,
            }}
            path="/"
            component={wrappedRoutes}
          />
        </Switch>
      </main>
    </MainWrapper>
  );
};


Router.propTypes = {
  childProps: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    loaded: PropTypes.bool.isRequired,
    isAuthenticated: PropTypes.bool.isRequired,
  }).isRequired,
  message: PropTypes.shape({}).isRequired,
  messages: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
  }).isRequired,
  toggleInboxDetail: PropTypes.func.isRequired,
  sidebar: SidebarProps.isRequired,
  toggleSidebarVisibility: PropTypes.func.isRequired,
  accounts: PropTypes.shape({}).isRequired,
  history: PropTypes.shape({
    listen: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
};

wrappedRoutes.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
  }).isRequired,
  messages: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
  }).isRequired,
  emptyMsg: PropTypes.func.isRequired,
  classes: PropTypes.element.isRequired,
  sidebar: SidebarProps.isRequired,
  toggleSidebarVisibility: PropTypes.func.isRequired,
  accounts: PropTypes.shape({}).isRequired,

};

export default compose(
  withRouter,
  connect(
    state => ({
      message: state.message,
      messages: state.messages,
      sidebar: state.sidebar,
    }),
    { toggleInboxDetail, toggleSidebarVisibility: changeSidebarVisibility },
  ),
)(Router);
